import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AnimationCard from '../components/AnimationCard';
import Spinner from '../components/Spinner';
import {
  fetchUserProfile,
  fetchUserAnimations,
  fetchFollowStatus,
  followUser,
  unfollowUser,
  type UserProfile as UserProfileData,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { UserAvatarChip } from '../lib/userDisplay';
import type { Animation } from '../types';

export default function UserProfile() {
  const { sub } = useParams<{ sub: string }>();
  const { user, login } = useAuth();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [animations, setAnimations] = useState<Animation[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const isOwnProfile = !!user && !!sub && user.sub === sub;

  useEffect(() => {
    if (!sub) return;
    setLoading(true);
    // sub（表示対象ユーザー）が変わったら一覧・カーソルを先頭からやり直す。
    setAnimations([]);
    setNextCursor(undefined);
    Promise.all([fetchUserProfile(sub), fetchUserAnimations(sub)])
      .then(([p, a]) => {
        setProfile(p);
        setAnimations(a.items);
        setNextCursor(a.nextCursor);
      })
      .finally(() => setLoading(false));
  }, [sub]);

  const handleLoadMore = () => {
    if (!sub || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    fetchUserAnimations(sub, { cursor: nextCursor })
      .then((a) => {
        setAnimations((prev) => [...prev, ...a.items]);
        setNextCursor(a.nextCursor);
      })
      .finally(() => setLoadingMore(false));
  };

  // フォロー中かどうかはログイン中かつ他人のプロフィールのときだけ分かる。
  useEffect(() => {
    if (!sub || !user || isOwnProfile) return;
    fetchFollowStatus(sub).then(setFollowing);
  }, [sub, user, isOwnProfile]);

  const toggleFollow = async () => {
    if (!sub) return;
    if (!user) {
      login();
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const res = following ? await unfollowUser(sub) : await followUser(sub);
      setFollowing(res.following);
    } catch (e) {
      setMessage(String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="container container--wide">
        <Spinner />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container container--wide">
        <h2>ユーザーが見つかりません</h2>
        <Link to="/" className="btn">
          ← 一覧へ戻る
        </Link>
      </div>
    );
  }

  return (
    <div className="container container--wide">
      <div className="user-profile__header">
        <UserAvatarChip iconUrl={profile.iconUrl} name={profile.nickname} size={72} />
        <div>
          <h1 className="user-profile__name">{profile.nickname}</h1>
          <div className="user-profile__meta">投稿 {animations.length} 件</div>
        </div>
        <div className="user-profile__actions">
          {isOwnProfile ? (
            <Link to="/me" className="btn">
              マイページへ
            </Link>
          ) : (
            <button
              type="button"
              className={`btn${following ? ' btn--primary' : ''}`}
              onClick={toggleFollow}
              disabled={busy}
            >
              {!user ? 'ログインしてフォロー' : following ? 'フォロー中' : 'フォロー'}
            </button>
          )}
        </div>
      </div>

      {message && <div className="stat">{message}</div>}

      <div className="section-title">
        <h2>投稿</h2>
      </div>

      {animations.length === 0 ? (
        <p>まだ投稿がありません。</p>
      ) : (
        <>
          <div className="grid">
            {animations.map((a) => (
              <AnimationCard key={a.id} animation={a} />
            ))}
          </div>

          {nextCursor && (
            <div className="load-more">
              <button type="button" className="btn" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? '読み込み中…' : 'もっと見る'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
